import React from 'react';
import { TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Controller, useFormContext } from 'react-hook-form';
import FormSpinner from '../../../../../components/FormSpinner';
import useIsFieldRequired from '../../../utility/useIsFieldRequired';

const RMSAutocomplete = ({
  name,
  label,
  getOptionLabel,
  options = [],
  optionLabel,
  optionValue,
  minSearchLength = 3,
  onSearch,
  loading,
  disabled,
  onSelectionChange,
  size = 'small',
  leftContent,
  rightContent,
  disableClearable,
  placeholder,
  autoFocus,
  multiple = false,
  hideSelectedTags = false,
  limitTags = -1,
}) => {
  const { control } = useFormContext();
  const isRequired = useIsFieldRequired(name);

  return (
    <>
      {loading && <FormSpinner />}

      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => {
          const selectedValue = multiple
            ? options.filter((option) => (field.value || []).includes(option[optionValue]))
            : optionValue
            ? options.find((option) => option?.[optionValue] === field.value) || null
            : field.value || null;

          const hasValue = multiple ? selectedValue.length > 0 : !!selectedValue;

          return (
            <Autocomplete
              options={options}
              disabled={disabled || field.disabled}
              value={selectedValue}
              multiple={multiple}
              renderTags={hideSelectedTags ? () => null : undefined}
              limitTags={limitTags}
              disableClearable={disableClearable}
              className={`rms-autocomplete ${hasValue ? 'has-value' : ''}`}
              onChange={(_, value) => {
                // Store only the key if optionValue is provided
                if (multiple) {
                  field.onChange(optionValue ? value.map((item) => item[optionValue]) : value);
                } else {
                  field.onChange(optionValue ? value?.[optionValue] ?? null : value);
                }

                // Always return the full object
                onSelectionChange?.(value);
              }}
              onInputChange={(_, value, reason) => {
                if (!onSearch) return;

                if (reason === 'input' && value?.trim()?.length >= minSearchLength) {
                  onSearch?.(value);
                }
              }}
              getOptionLabel={(option) =>
                getOptionLabel ? getOptionLabel(option) : option?.[optionLabel] || ''
              }
              getOptionSelected={(option, value) =>
                optionValue ? option?.[optionValue] === value?.[optionValue] : option === value
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus={autoFocus}
                  placeholder={!selectedValue ? placeholder : ''}
                  label={
                    <>
                      {label}
                      {isRequired && <span className="required-mark"> *</span>}
                    </>
                  }
                  size={size}
                  variant="outlined"
                  error={!!error}
                  helperText={error?.message}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    ...(placeholder && { shrink: true }),
                  }}
                  InputProps={{
                    ...params.InputProps,

                    startAdornment: (
                      <>
                        {leftContent?.({
                          value: selectedValue,
                          onChange: field.onChange,
                        })}

                        {params.InputProps.startAdornment}
                      </>
                    ),

                    endAdornment: (
                      <>
                        {rightContent?.({
                          value: selectedValue,
                          onChange: field.onChange,
                        })}

                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          );
        }}
      />
    </>
  );
};

export default RMSAutocomplete;
